/** @format */

import React, { memo } from 'react';

import { FormattedMessage } from 'gatsby-plugin-intl';
import moment from 'moment';
import styled from 'styled-components';
import { useAppTranslation } from '../../../../i18n/declarations';
import SuitableForCouples from '@/components/page/PageRoom/components/BookCard/SuitableForCouples';

interface Props {
  isSuitableForCouples?: boolean;
  oldPrice: number;
  endPromo: string;
  newPrice: number;
  isBookingFooter?: boolean;
  isCardRoom?: boolean;
  isMobileMapCard?: boolean;
  availability?: {
    availableFrom: string;
  };
}

export const RoomInfoDiscountPrice = memo(
  ({
    oldPrice,
    endPromo,
    newPrice,
    isBookingFooter,
    isCardRoom,
    isMobileMapCard,
    availability,
    isSuitableForCouples,
  }: Props) => {
    const { t } = useAppTranslation();

    return (
      <Container isBookingFooter={isBookingFooter} isCardRoom={isCardRoom}>
        <Row isBookingFooter={isBookingFooter}>
          <BasePrice $mobile={isMobileMapCard}>{oldPrice}€</BasePrice>
          <PromoPrice $mobile={isMobileMapCard}>
            <Price>
              <span className="promo_newprice">{newPrice}</span>
              <span className="promo_euro">€</span>
              <span className="promo_divider">/</span>
              <span className="promo_month">{t('month')}</span>
            </Price>
          </PromoPrice>
        </Row>
        <DatePromo>
          <FormattedMessage id="end_promo" values={{ endPromo }} />
        </DatePromo>
        <PromoDescription>
          <FormattedMessage id="old_price_promo" values={{ oldPrice }} />
        </PromoDescription>
        {isSuitableForCouples && <SuitableForCouples />}
        {!!availability?.availableFrom && (
          <PromoAvailabilityAvailableFrom>
            <FormattedMessage id="available_from" /> {moment(availability?.availableFrom).format('DD/MM/YYYY')}
          </PromoAvailabilityAvailableFrom>
        )}
      </Container>
    );
  },
);

const Container = styled.div<{ isBookingFooter: boolean | undefined; isCardRoom: boolean | undefined }>`
  font-size: 12px;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${({ isCardRoom }) => (isCardRoom ? 'flex-start' : ' center')};
  max-width: 300px;
  margin: 0 auto;
  margin-bottom: 25px;
  display: none;

  @media screen and (max-width: ${props => props.theme.breakpoints.lg}) {
    display: flex;
  }
`;

const Row = styled.div<{ isBookingFooter: boolean | undefined }>`
  display: flex;
  flex-direction: row;

  flex-wrap: nowrap;
  align-items: baseline;
  justify-content: ${({ isBookingFooter }) => (isBookingFooter ? 'center' : 'flex-start')};
`;

const BasePrice = styled.div<{ $mobile?: boolean }>`
  text-decoration: line-through;
  font-family: ${({ theme }) => theme.font.title};
  font-size: ${({ $mobile }) => ($mobile ? 0.75 : 0.875)}rem;
  color: #808080;

  margin-top: 4px;
  margin-right: 4px;

  text-align: center;
  font-size: 1rem;
  line-height: 168%; /* 26.88px */
  text-decoration-line: strikethrough;
`;

const PromoPrice = styled.div<{ $mobile?: boolean }>`
  color: ${({ theme }) => theme.colors.joivy.pink};
  font-family: ${({ theme }) => theme.font.title};
  font-size: ${({ $mobile }) => ($mobile ? 1 : 1.875)}rem;
`;

const DatePromo = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.joivy.black};
  font-size: 0.75rem;
`;

const PromoDescription = styled.div`
  color: ${({ theme }) => theme.colors.alternativeGrey};
  font-size: 0.75rem;
  line-height: 168%;
`;

const Price = styled.div`
  span {
    color: ${props => props.theme.colors.joivy.black};

    &.promo_newprice {
      color: ${props => props.theme.colors.green};
      font-size: 2rem;
      font-weight: 600;
      line-height: 168%;
    }

    &.promo_month {
      font-size: 1rem;
      font-weight: 400;
      line-height: 168%;
      color: ${props => props.theme.colors.joivy.black};
    }

    &.promo_divider {
      font-weight: 400;
      font-size: 1rem;
    }

    &.promo_euro {
      font-size: 1.5rem;
      color: ${props => props.theme.colors.green};
      font-weight: 400;
    }
  }
`;

const PromoAvailabilityAvailableFrom = styled.div`
  margin: 20px 0 0 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.joivy.pink};
  font-size: 14px;
  font-family: ${({ theme }) => theme.font.title};
`;
